// Dispatch › Hours (QA-402): the technicians' hours for a week, minutes only, never pay. A week runs
// Monday to Sunday; the board's link looks back seven days, where a missed clock-out can sit.

import { dateText } from '@flowology/kernel';
import { minutesText } from '../books/timesheet-model.ts';

export interface Range {
  from: string;
  to: string;
}

export interface TeamPerson {
  staffId: string;
  name: string;
  totals: { work: number; drive: number; nonJob: number };
  flagged: number;
  runningNow: number;
  leftOpen: number;
}

const addDays = (d: string, n: number) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** The Monday-to-Sunday week a day falls in. */
export function weekOf(day: string): Range {
  const dow = new Date(`${day}T12:00:00Z`).getUTCDay(); // 0 = Sunday
  const from = addDays(day, dow === 0 ? -6 : 1 - dow);
  return { from, to: addDays(from, 6) };
}

export const shiftWeek = (r: Range, weeks: number): Range => weekOf(addDays(r.from, weeks * 7));

/** The last seven days to today: the board's look back for a missed clock-out. */
export const lastSevenDays = (today: string): Range => ({ from: addDays(today, -6), to: today });

/** "Oct 12 – Oct 18". */
export const rangeText = (r: Range) =>
  `${dateText(r.from, 'UTC', { year: false })} – ${dateText(r.to, 'UTC', { year: false })}`;

/**
 * What the week picker shows (QA-519): a Monday-to-Sunday week, or the board's last seven days. It
 * is kept as chosen, never read back from the dates: on a Sunday this week and the last seven days
 * are the same days, and still two different choices.
 */
export type Picked =
  | { kind: 'week'; range: Range }
  | { kind: 'lastSeven'; range: Range }
  /** BL-126: one day, from a technician's clock line on the board: their timesheet for that day. */
  | { kind: 'day'; range: Range };

export const thisWeekPick = (today: string): Picked => ({ kind: 'week', range: weekOf(today) });
export const lastSevenPick = (today: string): Picked => ({ kind: 'lastSeven', range: lastSevenDays(today) });
export const dayPick = (day: string): Picked => ({ kind: 'day', range: { from: day, to: day } });

/** What the picker says it shows: "Oct 12 – Oct 18", the last seven days, or "Wednesday, Oct 21". */
export function pickText(p: Picked): string {
  if (p.kind === 'day') return dateText(p.range.from, 'UTC', { year: false, weekday: 'long' });
  return p.kind === 'lastSeven' ? `The last seven days, ${rangeText(p.range)}` : rangeText(p.range);
}

/** One week earlier or later, from the shown week's start; from the last seven days, the week before
 * this one or this one; from one day, the week before or after its own. */
export function stepPick(p: Picked, by: -1 | 1, today: string): Picked {
  if (p.kind === 'day') return { kind: 'week', range: shiftWeek(weekOf(p.range.from), by) };
  if (p.kind === 'lastSeven')
    return { kind: 'week', range: by < 0 ? shiftWeek(weekOf(today), -1) : weekOf(today) };
  return { kind: 'week', range: shiftWeek(p.range, by) };
}

/** "This week" is marked only when this week is the choice. */
export const isThisWeek = (p: Picked, today: string) =>
  p.kind === 'week' && p.range.from === weekOf(today).from;

/** No later week than this one (nor after a day of this week). */
export const laterDisabled = (p: Picked, today: string) =>
  (p.kind === 'week' && p.range.from >= weekOf(today).from) ||
  (p.kind === 'day' && weekOf(p.range.from).from >= weekOf(today).from);

/** A missed clock-out first, then flagged entries, then by name. */
export function teamOrder(people: readonly TeamPerson[]): TeamPerson[] {
  return [...people].sort(
    (a, b) =>
      Number(b.leftOpen > 0) - Number(a.leftOpen > 0) ||
      Number(b.flagged > 0) - Number(a.flagged > 0) ||
      a.name.localeCompare(b.name),
  );
}

/** "Work 6 h · Drive 1 h 30 min · Shop 0 min". */
export const totalsText = (t: TeamPerson['totals']) =>
  `Work ${minutesText(t.work)} · Drive ${minutesText(t.drive)} · Shop ${minutesText(t.nonJob)}`;

/** What needs a person's eye, in words, most pressing first. */
export function needs(p: TeamPerson): { text: string; tone: 'bad' | 'warn' | '' }[] {
  const out: { text: string; tone: 'bad' | 'warn' | '' }[] = [];
  if (p.leftOpen)
    out.push({
      text: p.leftOpen === 1 ? 'Clock-out missed' : `${p.leftOpen} clock-outs missed`,
      tone: 'bad',
    });
  if (p.flagged) out.push({ text: `${p.flagged} flagged`, tone: 'warn' });
  if (p.runningNow) out.push({ text: 'Working now', tone: '' });
  return out;
}
